/**
 * Integrator and API key management contracts (spec v3 §20).
 *
 * Spec v2 named the ApiIntegrator entity but never said how one gets onboarded. This is
 * that surface. The shape matters: onboarding the second integrator must be a console
 * operation, never a migration (D-007).
 */
import { z } from 'zod';
import { apiKeyEnvironmentSchema, apiScopeSchema, grantOrganisationScopeSchema, integratorStatusSchema, webhookEventSchema, } from './enums.js';
import { emailSchema, phoneSchema, slugSchema } from './primitives.js';
export const createIntegratorSchema = z.object({
    slug: slugSchema,
    name: z.string().trim().min(2).max(160),
    description: z.string().trim().max(1000).optional().nullable(),
    contactName: z.string().trim().max(120).optional().nullable(),
    contactEmail: emailSchema,
    contactPhone: phoneSchema.optional().nullable(),
    websiteUrl: z.string().url().max(500).optional().nullable(),
    defaultRateLimitPerMinute: z.number().int().min(1).max(100_000).default(600),
    defaultRateLimitBurst: z.number().int().min(1).max(10_000).default(60),
});
export const updateIntegratorSchema = createIntegratorSchema.partial().omit({ slug: true });
export const suspendIntegratorSchema = z.object({
    reason: z.string().trim().min(1, 'Say why — this appears in the audit trail').max(500),
});
/**
 * An IP allowlist entry. CIDR or a bare address. Empty list = no restriction, which is
 * the sensible default: an integrator running on autoscaled infrastructure often cannot
 * name its egress addresses, and a broken allowlist is worse than none.
 */
const cidrSchema = z
    .string()
    .trim()
    .regex(/^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$|^[0-9a-fA-F:]+(\/\d{1,3})?$/, {
    message: 'Enter an IP address or CIDR range',
});
export const createApiKeySchema = z.object({
    label: z.string().trim().min(1, 'Name the key so it can be identified later').max(120),
    environment: apiKeyEnvironmentSchema.default('LIVE'),
    /** Empty = inherit every scope the integrator's grants allow. */
    scopes: z.array(apiScopeSchema).default([]),
    ipAllowlist: z.array(cidrSchema).max(50).default([]),
    expiresAt: z.string().nullable().optional(),
    rateLimitPerMinute: z.number().int().min(1).max(100_000).nullable().optional(),
});
/**
 * The ONLY response that ever contains the secret. Shown once, behind an explicit
 * acknowledgement, and never retrievable again (§20.2).
 */
export const createdApiKeySchema = z.object({
    id: z.string(),
    keyId: z.string(),
    label: z.string(),
    environment: apiKeyEnvironmentSchema,
    /** Full `pvn_live_<keyId>_<secret>`. Not stored anywhere. */
    secret: z.string(),
    last4: z.string(),
    expiresAt: z.string().nullable(),
    createdAt: z.string(),
});
/**
 * Rotation issues a successor and expires the predecessor after an overlap. Immediate
 * revocation on rotation would break the integrator's production traffic the moment the
 * button is pressed, which is why nobody rotates credentials in systems that work that
 * way (§20.3).
 */
export const rotateApiKeySchema = z.object({
    overlapDays: z.number().int().min(0).max(90).default(30),
    label: z.string().trim().max(120).optional(),
});
export const revokeApiKeySchema = z.object({
    /** Typed confirmation of the key label. Revocation is immediate and irreversible. */
    confirmLabel: z.string().trim().min(1),
    reason: z.string().trim().min(1, 'Say why — this appears in the audit trail').max(500),
});
export const INTEGRATOR_OPERATIONS = [
    'SUBMIT',
    'POLL',
    'ACK',
    'RESPOND_CLARIFICATION',
    'ATTACH',
    'READ_DOCUMENT_TYPES',
];
export const integratorOperationSchema = z.enum(INTEGRATOR_OPERATIONS);
export const createGrantSchema = z.object({
    chamberId: z.string(),
    organisationScope: grantOrganisationScopeSchema.default('ALL'),
    organisationIds: z.array(z.string()).default([]),
    /** Empty = whatever the chamber currently has enabled. Narrowing only. */
    documentTypeIds: z.array(z.string()).default([]),
    operations: z.array(integratorOperationSchema).min(1).default([...INTEGRATOR_OPERATIONS]),
});
export const createWebhookEndpointSchema = z.object({
    /** HTTPS enforced outside local development — this payload carries certificate URLs. */
    url: z.string().url().max(2000),
    chamberId: z.string().nullable().optional(),
    events: z.array(webhookEventSchema).min(1),
    active: z.boolean().default(true),
    description: z.string().trim().max(300).optional().nullable(),
});
export const integratorSummarySchema = z.object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    status: integratorStatusSchema,
    contactEmail: z.string(),
    chamberCount: z.number().int(),
    activeKeyCount: z.number().int(),
    requests24h: z.number().int(),
    errorRate24h: z.number(),
    lastSeenAt: z.string().nullable(),
    createdAt: z.string(),
});
/**
 * Parses `pvn_live_<keyId>_<secret>`.
 *
 * Splits on the FIRST THREE underscores only, because the secret is base64url and
 * base64url legitimately contains `_`. A naive `split('_')` rejects roughly half of all
 * generated keys — which is exactly what it did, until a seeded key failed to
 * authenticate against the running API.
 *
 * Returns null rather than throwing: a malformed key is a client error with its own
 * error code, not an exception.
 */
export function parseApiKey(raw) {
    const match = /^([a-z0-9]+)_(live|test)_([0-9a-f]{8,})_(.+)$/.exec(raw.trim());
    if (!match)
        return null;
    const [, prefix, env, keyId, secret] = match;
    if (secret.length < 16)
        return null;
    return { prefix, environment: env === 'live' ? 'LIVE' : 'TEST', keyId, secret };
}
/**
 * The webhook signature header (§20.8). During a secret rotation both the current and
 * previous secret produce a valid `v1`, so an integrator can roll over without dropping
 * an event.
 */
export function buildSignatureHeader(timestamp, signatures) {
    return [`t=${timestamp}`, ...signatures.map((s) => `v1=${s}`)].join(',');
}
export function parseSignatureHeader(header) {
    const parts = header.split(',').map((p) => p.trim());
    let timestamp = null;
    const signatures = [];
    for (const part of parts) {
        const [k, v] = part.split('=');
        if (k === 't' && v)
            timestamp = Number(v);
        if (k === 'v1' && v)
            signatures.push(v);
    }
    if (timestamp === null || Number.isNaN(timestamp) || signatures.length === 0)
        return null;
    return { timestamp, signatures };
}
//# sourceMappingURL=integrator.js.map